import React, { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { DEFAULT_ENERGY_CALC, ENERGY_RULES, buildEnergyVars, evaluateEnergyRow, parseDMY } from '../../utils/energyCalc';

const newId = (p) => `${p}${Math.random().toString(36).slice(2, 7)}`;

const EnergyCalcEditor = ({ value, onChange }) => {
  const cfg = value && Array.isArray(value.tabs) ? value : DEFAULT_ENERGY_CALC;
  const [tabIdx, setTabIdx] = useState(0);
  const [previewBirth, setPreviewBirth] = useState('18.04.1991');
  const [previewTarget, setPreviewTarget] = useState('25.07.2026');

  const tab = cfg.tabs[Math.min(tabIdx, cfg.tabs.length - 1)];
  const set = (patch) => onChange({ ...cfg, ...patch });
  const setTabs = (tabs) => set({ tabs });
  const updateTab = (patch) => setTabs(cfg.tabs.map((t, i) => (i === tabIdx ? { ...t, ...patch } : t)));
  const updateRow = (ri, patch) => updateTab({ rows: tab.rows.map((r, i) => (i === ri ? { ...r, ...patch } : r)) });

  const vars = useMemo(() => {
    const b = parseDMY(previewBirth), t = parseDMY(previewTarget);
    return b && t ? buildEnergyVars(b, t) : null;
  }, [previewBirth, previewTarget]);

  return (
    <div className="admin-section" data-testid="energy-editor">
      <h3 className="admin-section-title">Karmik Enerji Hesaplama</h3>
      <p className="admin-section-desc">
        Tablar (İl / Gün / Ay) və hər tab üçün nəticə sətirləri. Tokenlər: <code>bday, bmonth, byear</code> (doğum),
        <code> tday, tmonth, tyear</code> (hədəf tarix). Funksiyalar: <code>digits(x)</code> rəqəm cəmi, <code>r22(x)</code> 22-qaydası.
        &quot;Hər ikisi&quot; qaydası: cəm 22-dən böyükdürsə <code>Arkan / Rəqəm</code> (42 → 20 / 6), əks halda olduğu kimi (20 → 20).
        Gün 22-dən böyükdürsə (məs. 31) sadə <code>bday</code> tokeni iki qolda hesablanır: Arkan qolunda 31→9, Rəqəm qolunda 31→4 (9+12+10+10=41→19 | 4+12+10+10=36→9 ⇒ <code>19 / 9</code>).
        <code>r22(bday)</code> və ya <code>digits(bday)</code> yazsanız hər iki qolda eyni qalır.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 p-3 rounded border border-yellow-500/20 bg-yellow-500/5">
        <label className="flex items-center gap-2 sm:col-span-2">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set({ enabled: e.target.checked })} data-testid="energy-enabled" />
          <span className="text-xs text-yellow-300/80">Aktiv — bu səhifənin/variantın blokunu göstər (Bölmələrdə &quot;Karmik Enerji&quot; tipi də əlavə edilə bilər)</span>
        </label>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs text-yellow-300/80">Başlıq</span>
          <input className="karma-input text-left" value={cfg.title || ''} onChange={(e) => set({ title: e.target.value })} data-testid="energy-title" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-white/60">Önizləmə doğum tarixi</span>
          <input className="karma-input text-left" value={previewBirth} onChange={(e) => setPreviewBirth(e.target.value)} data-testid="energy-preview-birth" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-white/60">Önizləmə hədəf tarixi</span>
          <input className="karma-input text-left" value={previewTarget} onChange={(e) => setPreviewTarget(e.target.value)} data-testid="energy-preview-target" />
        </label>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {cfg.tabs.map((t, i) => (
          <button key={t.id} type="button" onClick={() => setTabIdx(i)}
            className={`text-xs px-3 py-1.5 rounded border ${i === tabIdx ? 'border-cyan-400 bg-cyan-500/10 text-white' : 'border-white/10 text-white/60 hover:bg-white/5'}`}
            data-testid={`energy-editor-tab-${t.id}`}>
            {t.name || t.id}
          </button>
        ))}
        <button type="button" className="admin-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"
          onClick={() => { setTabs([...cfg.tabs, { id: newId('t'), name: 'Yeni', rows: [] }]); setTabIdx(cfg.tabs.length); }}
          data-testid="energy-add-tab">
          <Plus size={12} /> Tab
        </button>
      </div>

      {tab && (
        <div className="p-3 rounded border border-white/10 bg-black/30 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <label className="flex flex-col gap-1 flex-1">
              <span className="text-xs text-white/60">Tab adı</span>
              <input className="karma-input text-left" value={tab.name || ''} onChange={(e) => updateTab({ name: e.target.value })} data-testid="energy-tab-name" />
            </label>
            {cfg.tabs.length > 1 && (
              <button type="button" className="text-xs px-2 py-1 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 self-end"
                onClick={() => { setTabs(cfg.tabs.filter((_, i) => i !== tabIdx)); setTabIdx(0); }}
                data-testid="energy-delete-tab">
                Tabı sil
              </button>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {(tab.rows || []).map((r, ri) => {
              const res = vars ? evaluateEnergyRow(r, vars) : null;
              return (
                <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-2 rounded border border-white/10" data-testid={`energy-row-${r.id}`}>
                  <label className="flex flex-col gap-1 md:col-span-3">
                    <span className="text-[10px] text-white/50">Ad</span>
                    <input className="karma-input text-left" value={r.label || ''} onChange={(e) => updateRow(ri, { label: e.target.value })} data-testid={`energy-row-label-${r.id}`} />
                  </label>
                  <label className="flex flex-col gap-1 md:col-span-4">
                    <span className="text-[10px] text-white/50">Arkan formulu</span>
                    <input className="karma-input text-left font-mono text-xs" value={r.formula || ''} onChange={(e) => updateRow(ri, { formula: e.target.value })} data-testid={`energy-row-formula-${r.id}`} />
                  </label>
                  <label className="flex flex-col gap-1 md:col-span-3">
                    <span className="text-[10px] text-white/50">Qayda</span>
                    <select className="karma-input" value={r.rule || 'both'} onChange={(e) => updateRow(ri, { rule: e.target.value })} data-testid={`energy-row-rule-${r.id}`}>
                      {ENERGY_RULES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                  <div className="md:col-span-2 flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-cyan-300" title={res ? `Arkan xam: ${res.raw} · Rəqəm xam: ${res.rakamRaw}` : ''} data-testid={`energy-row-preview-${r.id}`}>
                      {res ? res.display : (vars ? '✗' : '—')}
                    </span>
                    <button type="button" className="p-1 text-red-400/70 hover:text-red-300"
                      onClick={() => updateTab({ rows: tab.rows.filter((_, i) => i !== ri) })} title="Sil">
                      <Trash2 size={12} />
                    </button>
                  </div>
                  {r.rule !== 'r22' && r.rule !== 'raw' && (
                    <label className="flex flex-col gap-1 md:col-span-12">
                      <span className="text-[10px] text-white/50">Rəqəm formulu <span className="text-white/30">(boş qalsa avtomatik: gün 22-dən böyükdürsə 31 → 3+1)</span></span>
                      <input className="karma-input text-left font-mono text-xs" style={{ maxWidth: 'none', width: '100%' }} value={r.formula2 || ''} placeholder="məs. digits(bday)+bmonth+tmonth+digits(tyear)"
                        onChange={(e) => updateRow(ri, { formula2: e.target.value })} data-testid={`energy-row-formula2-${r.id}`} />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
          <button type="button" className="admin-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1 self-start"
            onClick={() => updateTab({ rows: [...(tab.rows || []), { id: newId('r'), label: 'Yeni sətir', formula: 'digits(bday)+digits(bmonth)+digits(tyear)', rule: 'both' }] })}
            data-testid="energy-add-row">
            <Plus size={12} /> Sətir əlavə et
          </button>
        </div>
      )}
    </div>
  );
};

export default EnergyCalcEditor;
